import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

type AuctionWindowModalProps = {
  open: boolean;
  loading: boolean;
  saving: boolean;
  startAt: string | null;
  endAt: string | null;
  onClose: () => void;
  onSave: (payload: {
    Auction_Start_Date_Time__c: string;
    Auction_End_Date_Time__c: string;
  }) => Promise<void>;
};

const toInputValue = (isoValue: string | null) => {
  if (!isoValue) return "";
  const dt = new Date(isoValue);
  if (Number.isNaN(dt.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  const yyyy = dt.getFullYear();
  const mm = pad(dt.getMonth() + 1);
  const dd = pad(dt.getDate());
  const hh = pad(dt.getHours());
  const min = pad(dt.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

const toUtcIso = (value: string) => {
  const dt = new Date(value);
  return dt.toISOString();
};

const toDisplayValue = (isoValue: string | null) => {
  if (!isoValue) return "--";
  const dt = new Date(isoValue);
  if (Number.isNaN(dt.getTime())) return "--";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()}, ${pad(
    dt.getHours(),
  )}:${pad(dt.getMinutes())}`;
};

const AuctionWindowModal: React.FC<AuctionWindowModalProps> = ({
  open,
  loading,
  saving,
  startAt,
  endAt,
  onClose,
  onSave,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");

  useEffect(() => {
    if (!open) return;
    setEditMode(false);
    setStartLocal(toInputValue(startAt));
    setEndLocal(toInputValue(endAt));
  }, [open, startAt, endAt]);

  const canSave = useMemo(() => {
    if (!editMode || saving) return false;
    if (!startLocal || !endLocal) return false;
    const start = new Date(startLocal).getTime();
    const end = new Date(endLocal).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
    return end > start;
  }, [editMode, saving, startLocal, endLocal]);

  const resetEditValues = () => {
    setStartLocal(toInputValue(startAt));
    setEndLocal(toInputValue(endAt));
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "720px",
          borderRadius: "22px",
          background:
            "linear-gradient(165deg, rgba(13,18,29,0.98) 0%, rgba(7,10,18,0.99) 62%, rgba(8,14,30,0.98) 100%)",
          border: "1px solid rgba(255,255,255,0.18)",
          color: "#fff",
          boxShadow: "0 26px 68px rgba(0,0,0,0.62)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 2.2,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.14)",
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: "1.9rem" }}>
          Auction Window
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!editMode ? (
            <IconButton
              onClick={() => setEditMode(true)}
              disabled={loading || saving}
              sx={{
                color: "rgba(255,255,255,0.88)",
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(255,255,255,0.06)",
                "&:hover": { background: "rgba(255,255,255,0.12)" },
              }}
            >
              <EditOutlinedIcon />
            </IconButton>
          ) : (
            <Button
              variant="outlined"
              onClick={() => {
                resetEditValues();
                setEditMode(false);
              }}
              disabled={saving}
              sx={{
                textTransform: "none",
                borderColor: "rgba(255,255,255,0.34)",
                color: "#fff",
                fontWeight: 600,
              }}
              startIcon={<CloseIcon />}
            >
              Cancel Edit
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3.2, pb: 1.6 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={26} sx={{ color: "#FF8D1E" }} />
          </Box>
        ) : (
          <Box sx={{ display: "grid", gap: 3, mt: 4 }}>
            {editMode ? (
              <>
                <TextField
                  label="Auction Start Date/Time"
                  type="datetime-local"
                  fullWidth
                  value={startLocal}
                  disabled={saving}
                  onChange={(e) => setStartLocal(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    // mt: 0.5,
                    "& .MuiInputBase-root": {
                      borderRadius: "14px",
                      background:
                        "linear-gradient(180deg, rgba(8,10,15,0.92) 0%, rgba(11,13,20,0.92) 100%)",
                    },
                    "& .MuiInputBase-input": {
                      color: "#fff",
                      py: 1.6,
                      fontSize: "0.95rem",
                      pr: 5.2,
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.72)",
                      fontSize: "0.92rem",
                    },
                    "& input[type='datetime-local']::-webkit-calendar-picker-indicator":
                      {
                        filter: "invert(1) brightness(1.2)",
                        opacity: 0.95,
                        cursor: "pointer",
                        marginRight: "6px",
                      },
                  }}
                />
                <TextField
                  label="Auction End Date/Time"
                  type="datetime-local"
                  fullWidth
                  value={endLocal}
                  disabled={saving}
                  onChange={(e) => setEndLocal(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "14px",
                      background:
                        "linear-gradient(180deg, rgba(8,10,15,0.92) 0%, rgba(11,13,20,0.92) 100%)",
                    },
                    "& .MuiInputBase-input": {
                      color: "#fff",
                      py: 1.6,
                      fontSize: "0.95rem",
                      pr: 5.2,
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.72)",
                      fontSize: "0.92rem",
                    },
                    "& input[type='datetime-local']::-webkit-calendar-picker-indicator":
                      {
                        filter: "invert(1) brightness(1.2)",
                        opacity: 0.95,
                        cursor: "pointer",
                        marginRight: "6px",
                      },
                  }}
                />
              </>
            ) : (
              <>
                <Box>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.74)",
                      mb: 1,
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    Auction Start Date/Time
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid rgba(255,255,255,0.24)",
                      borderRadius: "14px",
                      px: 2.4,
                      py: 2.1,
                      color: "#fff",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      background:
                        "linear-gradient(90deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
                    }}
                  >
                    {toDisplayValue(startAt)}
                  </Box>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.74)",
                      mb: 1,
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    Auction End Date/Time
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid rgba(255,255,255,0.24)",
                      borderRadius: "14px",
                      px: 2.4,
                      py: 2.1,
                      color: "#fff",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      background:
                        "linear-gradient(90deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)",
                    }}
                  >
                    {toDisplayValue(endAt)}
                  </Box>
                </Box>
              </>
            )}
            {editMode && !canSave && (
              <Typography sx={{ color: "#ffb4b4", fontSize: "0.9rem" }}>
                End time must be greater than start time.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.6, pt: 1.2, gap: 1.2 }}>
        <Button
          onClick={() => {
            if (editMode) {
              resetEditValues();
              setEditMode(false);
              return;
            }
            onClose();
          }}
          disabled={saving}
          sx={{
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.22)",
            background: "rgba(255,255,255,0.08)",
            minWidth: 132,
            minHeight: 52,
            borderRadius: "10px",
            fontSize: "0.9rem",
            fontWeight: 700,
            textTransform: "none",
          }}
        >
          {editMode ? "Cancel" : "Close"}
        </Button>
        <Button
          disabled={!editMode || !canSave}
          onClick={async () => {
            await onSave({
              Auction_Start_Date_Time__c: toUtcIso(startLocal),
              Auction_End_Date_Time__c: toUtcIso(endLocal),
            });
            setEditMode(false);
          }}
          sx={{
            color: "#111",
            fontWeight: 700,
            minWidth: 132,
            minHeight: 52,
            borderRadius: "10px",
            fontSize: "0.9rem",
            textTransform: "none",
            background: "linear-gradient(90deg, #FF8D1E 0%, #FF4D7D 100%)",
          }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuctionWindowModal;
